// ============================================================
// Carte CO-OP : grille de salles OUVERTE et CONNECTÉE.
// Contrairement à la carte solo (branches type Slay-the-Spire),
// ici toutes les salles communiquent par des portes (N/S/E/O) et
// chaque joueur explore librement à son rythme.
//
// Modèle d'une salle :
//   {
//     key: "gx,gy", gx, gy, type,
//     neighbors: { N|S|E|W: "gx,gy" | null },
//     combatCleared: bool,         // global (combat/élite/boss) → clear pour TOUT le monde
//     clearedBy: ["uid", ...],     // par-joueur (coffre/sanctuaire/boutique)
//   }
// ============================================================

const TYPE_POOL = ["combat", "combat", "combat", "elite", "chest", "shop", "sanctuary"];

// Mulberry32 — PRNG déterministe seedé (réutilisé pour des cartes reproductibles si besoin)
const makeRng = (seed) => {
  let a = (seed | 0) || 1;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const keyOf = (gx, gy) => `${gx},${gy}`;
const DIRS = [
  ["N", 0, -1],
  ["S", 0, 1],
  ["W", -1, 0],
  ["E", 1, 0],
];

// BFS depuis startKey → distances, pour placer le boss le plus loin
const bfsDistances = (rooms, startKey) => {
  const dist = { [startKey]: 0 };
  const q = [startKey];
  while (q.length) {
    const k = q.shift();
    const r = rooms[k];
    for (const [, dx, dy] of DIRS) {
      const nk = keyOf(r.gx + dx, r.gy + dy);
      if (rooms[nk] && dist[nk] === undefined) {
        dist[nk] = dist[k] + 1;
        q.push(nk);
      }
    }
  }
  return dist;
};

export const generateCoopFloor = (floor = 1, seed = null) => {
  const rng = seed != null ? makeRng((seed * 1000003) ^ floor) : Math.random;
  // Nombre de salles croît avec l'étage (boss + start inclus), borné
  const targetRooms = Math.min(13, 6 + Math.floor(floor * 0.8));

  // 1) Placement par marche aléatoire connectée sur une grille
  const rooms = {};
  rooms[keyOf(0, 0)] = { gx: 0, gy: 0 };
  let placed = 1;
  let guard = 0;
  while (placed < targetRooms && guard < 2000) {
    guard++;
    const keys = Object.keys(rooms);
    const base = keys[Math.floor(rng() * keys.length)];
    const r = rooms[base];
    const [, dx, dy] = DIRS[Math.floor(rng() * 4)];
    const nk = keyOf(r.gx + dx, r.gy + dy);
    if (!rooms[nk]) {
      rooms[nk] = { gx: r.gx + dx, gy: r.gy + dy };
      placed++;
    }
  }

  // 2) Distances depuis la salle de départ → boss = la plus éloignée
  const startKey = keyOf(0, 0);
  const dist = bfsDistances(rooms, startKey);
  let bossKey = startKey;
  let maxD = -1;
  for (const k of Object.keys(rooms)) {
    if ((dist[k] ?? 0) > maxD) {
      maxD = dist[k] ?? 0;
      bossKey = k;
    }
  }

  // 3) Types : start = sanctuaire (spawn), boss = boss, le reste pondéré.
  //    On garantit au moins 1 boutique parmi les salles intermédiaires.
  const interKeys = Object.keys(rooms).filter((k) => k !== startKey && k !== bossKey);
  // shuffle déterministe
  for (let i = interKeys.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [interKeys[i], interKeys[j]] = [interKeys[j], interKeys[i]];
  }
  const typeFor = {};
  typeFor[startKey] = "sanctuary";
  typeFor[bossKey] = "boss";
  let hasShop = false;
  // Les Élites n'apparaissent qu'à partir de l'étage 5
  const floorPool = floor < 5 ? TYPE_POOL.filter((t) => t !== "elite") : TYPE_POOL;
  interKeys.forEach((k, idx) => {
    let t = floorPool[Math.floor(rng() * floorPool.length)];
    // Force une boutique si aucune n'a été placée et qu'on arrive en fin de liste
    if (!hasShop && idx === interKeys.length - 1) t = "shop";
    if (t === "shop") hasShop = true;
    typeFor[k] = t;
  });

  // 4) Construit l'objet final avec voisins
  const out = {};
  for (const k of Object.keys(rooms)) {
    const r = rooms[k];
    const neighbors = { N: null, S: null, W: null, E: null };
    for (const [dir, dx, dy] of DIRS) {
      const nk = keyOf(r.gx + dx, r.gy + dy);
      if (rooms[nk]) neighbors[dir] = nk;
    }
    out[k] = {
      key: k,
      gx: r.gx,
      gy: r.gy,
      type: typeFor[k],
      neighbors,
      combatCleared: false,
      clearedBy: [],
    };
  }

  return { rooms: out, startKey, bossKey, floor };
};

// Une salle "combat" (combat/élite/boss) ?
export const isCombatRoom = (type) => type === "combat" || type === "elite" || type === "boss";

// La salle est-elle déjà nettoyée pour ce joueur (interactable disparu) ?
export const isRoomClearedFor = (room, uid) => {
  if (!room) return true;
  if (isCombatRoom(room.type)) return !!room.combatCleared;
  return (room.clearedBy || []).includes(uid);
};

// Liste des portes disponibles depuis une salle : [{ dir, targetKey }]
export const doorsForRoom = (grid, roomKey) => {
  const room = grid?.rooms?.[roomKey];
  if (!room) return [];
  const out = [];
  for (const dir of ["N", "S", "W", "E"]) {
    if (room.neighbors[dir]) out.push({ dir, targetKey: room.neighbors[dir] });
  }
  return out;
};
